import { useReducer, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api, { apiHelpers } from '@/api';
import { useAuth } from '@/hooks/useAuth';
import { SAMPLE_REQUEST_STEPS, SampleRequestStep, CHAT_DRAFT_KEY } from '../chat.types';
import { User } from '@/types';
import { cosmeticAnalysisApi } from '@/api/cosmetic-analysis.api';
import { imagesApi } from '@/api/images.api';
import { encodeId } from '@/utils/idEncoder';
import { mapToSampleRequestFields } from '@/types/cosmetic-analysis.types';
import { sampleRequestFormReducer, initialState } from './sampleRequestFormReducer';

interface UseSampleRequestFormProps {
  onClose: () => void;
}

export const useSampleRequestForm = ({ onClose }: UseSampleRequestFormProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, userType, isAuthenticated, sendVerificationCode, verifyVerificationCode, brandLogin } = useAuth();

  const [state, dispatch] = useReducer(sampleRequestFormReducer, initialState);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Derived values
  const currentStepInfo = SAMPLE_REQUEST_STEPS[state.currentStep];
  const isCurrentStepRequired = currentStepInfo?.required;
  const currentValue = state.sampleFormData[currentStepInfo?.field] || '';
  const isImageStep = currentStepInfo?.type === 'image';
  const isEmailStep = currentStepInfo?.field === 'email';
  const emailVerificationRequired = isEmailStep && !isAuthenticated && !state.emailVerified;
  const hasImageValue = isImageStep ? !!(state.selectedImage || state.benchmarkImageUrl || state.imagePreview) : (currentValue && currentValue.toString().trim() !== '');
  const canProceed = (!isCurrentStepRequired || hasImageValue) && !emailVerificationRequired;

  const categories = Array.from(new Set(SAMPLE_REQUEST_STEPS.map(s => s.category)));
  const currentCategory = currentStepInfo?.category;
  const categoryIndex = categories.indexOf(currentCategory);

  const hasPrefilledInfo = isAuthenticated && userType === 'brand' && user;
  const prefilledFields = hasPrefilledInfo ? ['company_name', 'contact_person', 'email', 'phone', 'country_code'].filter(f => state.sampleFormData[f]) : [];

  // Initialize form
  const initializeForm = useCallback(() => {
    const savedState = sessionStorage.getItem(CHAT_DRAFT_KEY);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        dispatch({ type: 'RESTORE_FROM_SESSION', payload: { ...parsed, isAuthenticated } });
        return;
      } catch (e) {
        console.error('Failed to parse saved chat state:', e);
        sessionStorage.removeItem(CHAT_DRAFT_KEY);
      }
    }

    let initialFormData: Record<string, any> = {};
    let firstEmptyStep = 0;
    let emailVerified = false;

    if (isAuthenticated && user && userType === 'brand') {
      const brandUser = user as User;
      initialFormData = {
        company_name: brandUser.company_name || '',
        contact_person: brandUser.contact_person || '',
        email: brandUser.email || '',
        phone: brandUser.phone || '',
      };
      emailVerified = true;

      for (let i = 0; i < SAMPLE_REQUEST_STEPS.length; i++) {
        const field = SAMPLE_REQUEST_STEPS[i].field;
        if (!initialFormData[field] || initialFormData[field].toString().trim() === '') {
          firstEmptyStep = i;
          break;
        }
      }
    } else if (isAuthenticated && userType === 'admin') {
      emailVerified = true;
    }

    dispatch({ type: 'INITIALIZE_DEFAULT', payload: { formData: initialFormData, firstEmptyStep, emailVerified } });
  }, [isAuthenticated, user, userType]);

  // Save form state to sessionStorage
  useEffect(() => {
    if (state.showSuccessScreen || state.showDraftSuccessScreen) return;
    const hasData = Object.keys(state.sampleFormData).some(k => state.sampleFormData[k]);
    const hasBenchmark = state.benchmarkImageUrl || state.benchmarkAnalysisResult;
    if (!hasData && !hasBenchmark && state.inputMode === 'select') return;

    const stateToSave = {
      formData: state.sampleFormData,
      currentStep: state.currentStep,
      inputMode: state.inputMode,
      benchmarkImageUrl: state.benchmarkImageUrl,
      benchmarkImagePreview: state.benchmarkImagePreview,
      benchmarkAnalysisResult: state.benchmarkAnalysisResult,
      savedDraftId: state.savedDraftId,
      imagePreview: state.imagePreview,
      savedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(CHAT_DRAFT_KEY, JSON.stringify(stateToSave));
  }, [state.sampleFormData, state.currentStep, state.inputMode, state.benchmarkImageUrl, state.benchmarkImagePreview, state.benchmarkAnalysisResult, state.savedDraftId, state.imagePreview, state.showSuccessScreen, state.showDraftSuccessScreen]);

  // Reset form
  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET_FORM' });
    sessionStorage.removeItem(CHAT_DRAFT_KEY);
  }, []);

  // Handle step input
  const handleSampleStepInput = useCallback((value: any) => {
    const currentField = SAMPLE_REQUEST_STEPS[state.currentStep].field;
    dispatch({ type: 'SET_FORM_FIELD', payload: { field: currentField, value } });
  }, [state.currentStep]);

  // Image handling
  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        dispatch({ type: 'SET_SELECTED_IMAGE', payload: { file, preview: reader.result as string } });
      };
      reader.readAsDataURL(file);
      handleSampleStepInput(file.name);
    }
  }, [handleSampleStepInput]);

  const handleRemoveImage = useCallback(() => {
    dispatch({ type: 'SET_SELECTED_IMAGE', payload: { file: null, preview: null } });
    dispatch({ type: 'CLEAR_BENCHMARK' });
    handleSampleStepInput('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleSampleStepInput]);

  const handleImageDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({ type: 'SET_IMAGE_DRAGGING', payload: true });
  }, []);

  const handleImageDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({ type: 'SET_IMAGE_DRAGGING', payload: false });
  }, []);

  const handleImageDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({ type: 'SET_IMAGE_DRAGGING', payload: false });

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!file.type.startsWith('image/')) {
        toast.error('Please drop an image file');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        dispatch({ type: 'SET_SELECTED_IMAGE', payload: { file, preview: reader.result as string } });
      };
      reader.readAsDataURL(file);
      handleSampleStepInput(file.name);
    }
  }, [handleSampleStepInput]);

  // Navigation
  const handleNextStep = useCallback(() => {
    if (state.currentStep < SAMPLE_REQUEST_STEPS.length - 1) {
      dispatch({ type: 'NEXT_STEP' });
    }
  }, [state.currentStep]);

  const handlePrevStep = useCallback(() => {
    if (state.currentStep > 0) {
      dispatch({ type: 'PREV_STEP' });
    }
  }, [state.currentStep]);

  const handleSkipStep = useCallback(() => {
    if (state.currentStep < SAMPLE_REQUEST_STEPS.length - 1) {
      const currentField = SAMPLE_REQUEST_STEPS[state.currentStep].field;
      dispatch({ type: 'SKIP_STEP', payload: { currentField, totalSteps: SAMPLE_REQUEST_STEPS.length } });
    }
  }, [state.currentStep]);

  // Email verification
  const handleSendVerificationCode = useCallback(async () => {
    const email = state.sampleFormData.email;
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      dispatch({ type: 'SEND_VERIFICATION_START' });
      await sendVerificationCode(email, 'login');
      dispatch({ type: 'SEND_VERIFICATION_SUCCESS' });
      toast.success('Verification code sent to your email');
    } catch (error: any) {
      dispatch({ type: 'SEND_VERIFICATION_FAIL' });
      toast.error(error?.message || 'Failed to send verification code');
    }
  }, [state.sampleFormData.email, sendVerificationCode]);

  const handleVerifyCode = useCallback(async () => {
    const email = state.sampleFormData.email;
    if (!state.verificationCode || state.verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit verification code');
      return;
    }

    try {
      dispatch({ type: 'VERIFY_EMAIL_START' });
      const result = await verifyVerificationCode(email, state.verificationCode);

      dispatch({ type: 'VERIFY_EMAIL_SUCCESS' });

      if (result && !result.newUser) {
        toast.success('Email verified! Your info has been loaded.');
      } else if (result?.newUser) {
        toast.success('Email verified! Please fill in your company details.');
        if (state.currentStep === 2) {
          dispatch({ type: 'NEXT_STEP' });
        }
      }
    } catch (error: any) {
      dispatch({ type: 'VERIFY_EMAIL_FAIL' });
      toast.error(error?.message || 'Invalid verification code');
    }
  }, [state.sampleFormData.email, state.verificationCode, verifyVerificationCode, state.currentStep]);

  // Password login (brand → admin fallback)
  const handlePasswordLogin = useCallback(async () => {
    const email = state.sampleFormData.email;
    if (!email || !state.loginPassword) return;

    try {
      dispatch({ type: 'PASSWORD_LOGIN_START' });
      await brandLogin(email, state.loginPassword);
      dispatch({ type: 'PASSWORD_LOGIN_SUCCESS' });
      toast.success('Login successful! Your info has been loaded.');
    } catch (error: any) {
      dispatch({ type: 'PASSWORD_LOGIN_FAIL' });
      toast.error(error?.message || 'Invalid email or password');
    }
  }, [state.sampleFormData.email, state.loginPassword, brandLogin]);

  // Benchmark image upload
  const handleBenchmarkImageUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    dispatch({ type: 'BENCHMARK_UPLOAD_START' });
    try {
      const result = await imagesApi.uploadImage(file, 'benchmark-analysis');
      dispatch({ type: 'BENCHMARK_UPLOAD_SUCCESS', payload: {
        url: result.url,
        preview: result.url,
        storageKey: result.storage_key || '',
      }});
      toast.success('Image uploaded. Click "Analyze" to extract product info.');
    } catch (error) {
      dispatch({ type: 'BENCHMARK_UPLOAD_FAIL' });
      toast.error('Failed to upload image');
    }
  }, []);

  // Benchmark image analysis
  const handleAnalyzeBenchmark = useCallback(async () => {
    if (!state.benchmarkImageUrl) return;

    dispatch({ type: 'BENCHMARK_ANALYZE_START' });
    try {
      const result = await cosmeticAnalysisApi.analyze(state.benchmarkImageUrl);
      const mappedFields = mapToSampleRequestFields(result);
      const mergedFormData = {
        ...state.sampleFormData,
        product_name: mappedFields.product_name || state.sampleFormData.product_name,
        product_type: mappedFields.product_type || state.sampleFormData.product_type,
        volume: mappedFields.volume || state.sampleFormData.volume,
        efficacy_function: mappedFields.efficacy_function || state.sampleFormData.efficacy_function,
        formula_texture: mappedFields.formula_texture || state.sampleFormData.formula_texture,
        key_promotional_ingredients: mappedFields.key_promotional_ingredients || state.sampleFormData.key_promotional_ingredients,
        restricted_ingredients: mappedFields.restricted_ingredients || state.sampleFormData.restricted_ingredients,
        marketing_brand_claims: mappedFields.marketing_brand_claims || state.sampleFormData.marketing_brand_claims,
        fragrance: mappedFields.fragrance || state.sampleFormData.fragrance,
        color: mappedFields.color || state.sampleFormData.color,
        primary_packaging: mappedFields.primary_packaging || state.sampleFormData.primary_packaging,
        secondary_packaging: mappedFields.secondary_packaging || state.sampleFormData.secondary_packaging,
        target_market_country: mappedFields.target_market_country || state.sampleFormData.target_market_country,
        // benchmark_product_link 에 분석 이미지 URL 을 넣지 않음 — 이미지는 reference_packaging_image 로 내부 전달
      };

      dispatch({ type: 'BENCHMARK_ANALYZE_SUCCESS', payload: { result, mergedFormData } });
      toast.success('Analysis complete! Fields have been auto-filled.');
    } catch (error: any) {
      const reason = error.response?.data?.data?.reason;
      dispatch({ type: 'BENCHMARK_ANALYZE_FAIL', payload: reason || 'Failed to analyze image' });
      toast.error(reason || 'Failed to analyze image');
    }
  }, [state.benchmarkImageUrl, state.sampleFormData]);

  // Proceed from analysis
  const handleProceedFromAnalysis = useCallback(() => {
    const firstEmptyRequiredStep = SAMPLE_REQUEST_STEPS.findIndex(
      step => step.required && !state.sampleFormData[step.field]?.toString().trim()
    );
    dispatch({ type: 'PROCEED_FROM_ANALYSIS', payload: { step: firstEmptyRequiredStep >= 0 ? firstEmptyRequiredStep : 0 } });
  }, [state.sampleFormData]);

  // Clear benchmark image
  const handleClearBenchmarkImage = useCallback(() => {
    dispatch({ type: 'CLEAR_BENCHMARK' });
  }, []);

  // Get missing required fields
  const getMissingRequiredFields = useCallback(() => {
    return SAMPLE_REQUEST_STEPS.filter(step =>
      step.required && !state.sampleFormData[step.field]?.toString().trim()
    );
  }, [state.sampleFormData]);

  // Redirect to form page
  const handleRedirectToFormPage = useCallback((missingFields: SampleRequestStep[]) => {
    const draftData = {
      ...state.sampleFormData,
      _savedAt: new Date().toISOString(),
      _fromChat: true
    };
    sessionStorage.setItem(CHAT_DRAFT_KEY, JSON.stringify(draftData));

    const queryParams = new URLSearchParams();
    Object.entries(state.sampleFormData).forEach(([key, value]) => {
      if (value && typeof value === 'string') {
        queryParams.set(key, value);
      }
    });

    const fieldLabels = missingFields.map(f => f.label).join(', ');
    toast.error(`Please complete required fields: ${fieldLabels}`, { duration: 4000 });

    navigate(`/sample-request?${queryParams.toString()}`);
    onClose();
  }, [state.sampleFormData, navigate, onClose]);

  // Submit sample request
  const handleSubmitSampleRequest = useCallback(async () => {
    const missingFields = getMissingRequiredFields();
    if (missingFields.length > 0) {
      handleRedirectToFormPage(missingFields);
      return;
    }

    dispatch({ type: 'SUBMIT_START' });
    try {
      const formData: Record<string, any> = { ...state.sampleFormData };

      if (state.selectedImage) {
        formData.image_file = state.selectedImage;
      }

      if (state.benchmarkImageUrl && !state.selectedImage) {
        formData.reference_packaging_image = state.benchmarkImageUrl;
        if (state.benchmarkStorageKey) {
          formData.storage_key = state.benchmarkStorageKey;
        }
      }

      if (formData.estimated_launch_quantity) {
        formData.estimated_launch_quantity = parseInt(formData.estimated_launch_quantity, 10);
      }
      if (formData.target_cost_budget) {
        formData.target_cost_budget = parseFloat(formData.target_cost_budget);
      }

      const response = await apiHelpers.sampleRequests.create(formData);
      const result = response.data?.data;

      if (result?.request_code) {
        dispatch({ type: 'SUBMIT_SUCCESS', payload: {
          requestCode: result.request_code,
          id: result.id,
        }});
        toast.success('Sample request submitted successfully!');
        sessionStorage.removeItem(CHAT_DRAFT_KEY);
        queryClient.invalidateQueries({ queryKey: ['myRequests'] });
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['adminSampleRequests'] });
        queryClient.invalidateQueries({ queryKey: ['adminAllProjects'] });
      } else {
        throw new Error('Failed to get request code');
      }
    } catch (error: any) {
      dispatch({ type: 'SUBMIT_FAIL' });
      console.error('Error submitting sample request:', error);
      toast.error(error.response?.data?.message || 'Failed to submit sample request');
    }
  }, [state.sampleFormData, state.selectedImage, state.benchmarkImageUrl, state.benchmarkStorageKey, getMissingRequiredFields, handleRedirectToFormPage, queryClient]);

  // Save draft
  const handleSaveDraft = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Please verify your email to save draft');
      return;
    }

    if (userType !== 'brand' && userType !== 'admin') {
      toast.error('Please log in to save drafts.');
      return;
    }

    const filledFields = Object.keys(state.sampleFormData).filter(k => state.sampleFormData[k]);
    if (filledFields.length === 0) {
      toast.error('Please fill in at least one field before saving');
      return;
    }

    dispatch({ type: 'SAVE_DRAFT_START' });
    try {
      const formData = new FormData();
      Object.keys(state.sampleFormData).forEach(key => {
        const value = state.sampleFormData[key];
        if (value !== null && value !== undefined && value !== '') {
          formData.append(key, value);
        }
      });

      if (state.selectedImage) {
        formData.append('image_file', state.selectedImage);
      }

      if (state.benchmarkImageUrl && !state.selectedImage) {
        formData.append('reference_packaging_image', state.benchmarkImageUrl);
        if (state.benchmarkStorageKey) {
          formData.append('storage_key', state.benchmarkStorageKey);
        }
      }

      formData.append('status', 'saved');

      // Admin: attach selected brand ID (backend falls back to NextPangaea if not provided)
      if (userType === 'admin' && state.selectedBrandId) {
        formData.append('brandId', String(state.selectedBrandId));
      }

      let response;
      if (state.savedDraftId) {
        // brand는 /save-draft (in-place), admin은 PUT 유지 (백엔드 가드가 in-place 처리)
        if (userType === 'admin') {
          response = await api.put(`/admin/sample-requests/${state.savedDraftId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          response = await api.post(`/sample-requests/${state.savedDraftId}/save-draft`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      } else {
        const createUrl = userType === 'admin'
          ? '/admin/sample-requests/draft'
          : '/sample-requests/draft';
        response = await api.post(createUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      const result = response.data?.data || response.data?.sampleRequest || response.data;
      if (result?.id) {
        dispatch({ type: 'SAVE_DRAFT_SUCCESS', payload: {
          id: result.id,
          requestCode: result.request_code || `DRAFT-${result.id}`,
        }});
        sessionStorage.removeItem(CHAT_DRAFT_KEY);
        toast.success('Draft saved successfully!');
        queryClient.invalidateQueries({ queryKey: ['myDrafts'] });
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['myRequests'] });
        queryClient.invalidateQueries({ queryKey: ['adminSampleRequests'] });
        queryClient.invalidateQueries({ queryKey: ['adminAllProjects'] });
      }
    } catch (error: any) {
      dispatch({ type: 'SAVE_DRAFT_FAIL' });
      console.error('Error saving draft:', error);
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.message || 'Failed to save draft');
      }
    }
  }, [isAuthenticated, userType, state.sampleFormData, state.selectedImage, state.benchmarkImageUrl, state.benchmarkStorageKey, state.savedDraftId, state.selectedBrandId, queryClient]);

  // Copy to clipboard
  const handleCopyToClipboard = useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch({ type: 'SET_COPIED_FIELD', payload: field });
      toast.success('Copied to clipboard!');
      setTimeout(() => dispatch({ type: 'SET_COPIED_FIELD', payload: null }), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  }, []);

  const handleGoToDetail = useCallback(() => {
    if (state.submittedRequest?.id) {
      navigate(`/requests/${encodeId(state.submittedRequest.id)}`);
      onClose();
    }
  }, [state.submittedRequest, navigate, onClose]);

  const handleGoToDraft = useCallback(() => {
    if (state.savedDraftInfo?.id) {
      navigate(`/requests/${encodeId(state.savedDraftInfo.id)}`);
      onClose();
    }
  }, [state.savedDraftInfo, navigate, onClose]);

  // Pre-fill form when user becomes authenticated
  useEffect(() => {
    if (state.emailVerified && isAuthenticated && user && userType === 'brand') {
      const brandUser = user as User;
      dispatch({ type: 'AUTH_PREFILL', payload: {
        formData: {
          company_name: brandUser.company_name || '',
          contact_person: brandUser.contact_person || '',
          email: brandUser.email || '',
          phone: brandUser.phone || '',
        },
        minStep: 4,
      }});
    }
  }, [state.emailVerified, isAuthenticated, user, userType]);

  // Reset email verification on logout
  useEffect(() => {
    if (!isAuthenticated) {
      dispatch({ type: 'AUTH_LOGOUT_RESET' });
    }
  }, [isAuthenticated]);

  return {
    // State
    currentStep: state.currentStep,
    currentStepInfo,
    sampleFormData: state.sampleFormData,
    selectedImage: state.selectedImage,
    imagePreview: state.imagePreview,
    isImageDragging: state.isImageDragging,
    isSubmitting: state.isSubmitting,
    isSavingDraft: state.isSavingDraft,
    savedDraftId: state.savedDraftId,
    savedDraftInfo: state.savedDraftInfo,
    submittedRequest: state.submittedRequest,
    showSuccessScreen: state.showSuccessScreen,
    showDraftSuccessScreen: state.showDraftSuccessScreen,
    copiedField: state.copiedField,
    emailVerificationSent: state.emailVerificationSent,
    verificationCode: state.verificationCode,
    isVerifyingEmail: state.isVerifyingEmail,
    isSendingVerification: state.isSendingVerification,
    emailVerified: state.emailVerified,
    showPasswordLogin: state.showPasswordLogin,
    loginPassword: state.loginPassword,
    isPasswordLoggingIn: state.isPasswordLoggingIn,
    fileInputRef,
    canProceed,
    categories,
    currentCategory,
    categoryIndex,
    hasPrefilledInfo,
    prefilledFields,
    isAuthenticated,
    userType,
    // Benchmark states
    inputMode: state.inputMode,
    benchmarkImageUrl: state.benchmarkImageUrl,
    benchmarkImagePreview: state.benchmarkImagePreview,
    isUploadingBenchmark: state.isUploadingBenchmark,
    isAnalyzingBenchmark: state.isAnalyzingBenchmark,
    benchmarkAnalysisResult: state.benchmarkAnalysisResult,
    analysisError: state.analysisError,
    // Admin brand selection
    selectedBrandId: state.selectedBrandId,

    // Setters (dispatch wrappers for backward compatibility)
    setVerificationCode: (code: string) => dispatch({ type: 'SET_VERIFICATION_CODE', payload: code }),
    setShowPasswordLogin: (v: boolean) => dispatch({ type: 'TOGGLE_PASSWORD_LOGIN', payload: v }),
    setLoginPassword: (pw: string) => dispatch({ type: 'SET_LOGIN_PASSWORD', payload: pw }),
    setShowSuccessScreen: (v: boolean) => dispatch({ type: 'SET_SHOW_SUCCESS_SCREEN', payload: v }),
    setShowDraftSuccessScreen: (v: boolean) => dispatch({ type: 'SET_SHOW_DRAFT_SUCCESS_SCREEN', payload: v }),
    setSampleFormData: (updaterOrValue: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => {
      if (typeof updaterOrValue === 'function') {
        dispatch({ type: 'SET_FORM_DATA', payload: updaterOrValue(state.sampleFormData) });
      } else {
        dispatch({ type: 'SET_FORM_DATA', payload: updaterOrValue });
      }
    },
    setSelectedBrandId: (id: number | null) => dispatch({ type: 'SET_SELECTED_BRAND_ID', payload: id }),
    setInputMode: (mode: 'select' | 'manual' | 'benchmark') => dispatch({ type: 'SET_INPUT_MODE', payload: mode }),
    setBenchmarkImagePreview: (url: string) => dispatch({ type: 'BENCHMARK_UPLOAD_SUCCESS', payload: { url: state.benchmarkImageUrl, preview: url, storageKey: state.benchmarkStorageKey } }),
    setBenchmarkImageUrl: (url: string) => dispatch({ type: 'BENCHMARK_UPLOAD_SUCCESS', payload: { url, preview: state.benchmarkImagePreview, storageKey: state.benchmarkStorageKey } }),
    setBenchmarkAnalysisResult: (result: any) => dispatch({ type: 'BENCHMARK_ANALYZE_SUCCESS', payload: { result, mergedFormData: state.sampleFormData } }),

    // Handlers
    initializeForm,
    resetForm,
    handleSampleStepInput,
    handleImageSelect,
    handleRemoveImage,
    handleImageDragOver,
    handleImageDragLeave,
    handleImageDrop,
    handleNextStep,
    handlePrevStep,
    handleSkipStep,
    handleSendVerificationCode,
    handleVerifyCode,
    handlePasswordLogin,
    handleSubmitSampleRequest,
    handleSaveDraft,
    handleCopyToClipboard,
    handleGoToDetail,
    handleGoToDraft,
    // Benchmark handlers
    handleBenchmarkImageUpload,
    handleAnalyzeBenchmark,
    handleProceedFromAnalysis,
    handleClearBenchmarkImage,
  };
};
